/**
 * Pulls the server's `{ "error": "…" }` detail out of a failed request.
 *
 * The admin endpoints answer a rejected call with that shape — `batch-update-bodies`, for
 * instance, names the duplicate or unknown lesson id — and a catch-all "something failed"
 * toast throws that away, leaving a failed migration very hard to diagnose.
 *
 * Kept free of axios and React imports so it is trivially unit-testable, matching the
 * convention in components/reorder.ts.
 */
export function serverErrorDetail(err: unknown): string | null {
  const response = readProperty(err, 'response');
  const data = readProperty(response, 'data');
  const detail = readProperty(data, 'error');

  return typeof detail === 'string' && detail.trim() ? detail.trim() : null;
}

/** Appends the server's detail to a fallback message when there is one to append. */
export function withServerDetail(fallback: string, err: unknown): string {
  const detail = serverErrorDetail(err);
  return detail ? `${fallback} — ${detail}` : fallback;
}

function readProperty(value: unknown, key: string): unknown {
  if (typeof value !== 'object' || value === null) return undefined;
  return (value as Record<string, unknown>)[key];
}
